/* Must also be on the Supabase Auth redirect allow-list (docs/ios-release.md). */
export const NATIVE_AUTH_CALLBACK_URL = "com.getparallel.braindump://auth/callback";

const parseUrl = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

/* Returns the callback's query params when the url is the sign-in redirect, else null. */
export const appCallbackFromNativeUrl = (url: string): URLSearchParams | null => {
  const parsed = parseUrl(url);
  const expected = new URL(NATIVE_AUTH_CALLBACK_URL);
  if (
    !parsed ||
    parsed.protocol !== expected.protocol ||
    parsed.host !== expected.host ||
    parsed.pathname !== expected.pathname
  ) {
    return null;
  }
  return parsed.searchParams;
};

export type AuthSheetOutcome = "completed" | "dismissed";

export const NATIVE_SIGN_IN_PENDING_KEY = "brain-dump:native-sign-in-started-at";
export const NATIVE_SIGN_IN_PENDING_TTL_MS = 10 * 60 * 1000;

let settleOpenSheet: ((outcome: AuthSheetOutcome) => void) | null = null;

/*
 * Only a sign-in the app started may consume a callback, since any page can open the scheme.
 * Kept in localStorage, not memory: iOS can relaunch the web view while the sheet is up.
 */
export const isNativeSignInPending = (): boolean => {
  const startedAt = Number(localStorage.getItem(NATIVE_SIGN_IN_PENDING_KEY));
  return startedAt > 0 && Date.now() - startedAt < NATIVE_SIGN_IN_PENDING_TTL_MS;
};

const clearNativeSignInPending = (): void =>
  localStorage.removeItem(NATIVE_SIGN_IN_PENDING_KEY);

/* Google refuses OAuth inside a WKWebView, so sign-in runs in an in-app Safari sheet. */
export const presentAuthSheet = async ({ url }: { url: string }): Promise<AuthSheetOutcome> => {
  const { Browser } = await import("@capacitor/browser");
  let listener: Awaited<ReturnType<typeof Browser.addListener>> | null = null;
  try {
    const closed = new Promise<AuthSheetOutcome>((resolve) => {
      settleOpenSheet = resolve;
    });
    localStorage.setItem(NATIVE_SIGN_IN_PENDING_KEY, String(Date.now()));
    listener = await Browser.addListener("browserFinished", () =>
      settleOpenSheet?.("dismissed"),
    );
    await Browser.open({ url });
    return await closed;
  } finally {
    settleOpenSheet = null;
    clearNativeSignInPending();
    await listener?.remove();
  }
};

/* Browser.close() never fires browserFinished, so the redirect's arrival settles the sheet itself. */
export const completeAuthSheet = async (): Promise<void> => {
  const settle = settleOpenSheet;
  clearNativeSignInPending();
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.close();
  } catch (error) {
    console.warn("closing the sign-in sheet failed", String(error));
  } finally {
    settle?.("completed");
  }
};
